import { useCallback, useMemo } from 'react';
import { useAppSelector, useAppDispatch } from '../redux/store';
import { updateUser } from '../redux/slices/userSlice';
import { userSelectOneObjectByQuery } from '../redux/thunks/DBServiceThunks';
import { userLayoutService } from '../services/userLayoutService';
import type { User } from '../models/user';

// Default values
const DEFAULT_SIDEBAR_WIDTH = 20; // percentage (approximately 280px on 1400px screen)
const DEFAULT_KNOWLEDGEBASE_WIDTH = 28; // percentage
const DEFAULT_MIN_SIDEBAR_WIDTH = 200; // pixels
const DEFAULT_MIN_KNOWLEDGEBASE_WIDTH = 250; // pixels

/**
 * Hook to handle layout preferences with database persistence
 */
export const useLayoutPreferences = () => {
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.UserReducer.user);
  const dbUser = useAppSelector((state) =>
    userSelectOneObjectByQuery(state.DBUserReducer, { _id: user?._id })
  );

  // Get layout preferences from DB user (priority) or local user, with defaults
  const layoutPreferences = useMemo((): User['layoutPreferences'] => {
    const prefs = dbUser?.layoutPreferences || user?.layoutPreferences;
    
    return {
      sidebarWidth: prefs?.sidebarWidth ?? DEFAULT_SIDEBAR_WIDTH,
      knowledgebaseWidth: prefs?.knowledgebaseWidth ?? DEFAULT_KNOWLEDGEBASE_WIDTH,
      minWidths: {
        sidebar: prefs?.minWidths?.sidebar ?? DEFAULT_MIN_SIDEBAR_WIDTH,
        knowledgebase: prefs?.minWidths?.knowledgebase ?? DEFAULT_MIN_KNOWLEDGEBASE_WIDTH,
      },
    };
  }, [dbUser?.layoutPreferences, user?.layoutPreferences]);

  // Get minimum widths from config
  const minWidths = useMemo(() => {
    return {
      sidebar: layoutPreferences.minWidths?.sidebar ?? DEFAULT_MIN_SIDEBAR_WIDTH,
      knowledgebase: layoutPreferences.minWidths?.knowledgebase ?? DEFAULT_MIN_KNOWLEDGEBASE_WIDTH,
    };
  }, [layoutPreferences.minWidths]);

  /**
   * Update layout preferences
   * Updates Redux immediately and triggers debounced DB save
   */
  const updateLayoutPreferences = useCallback(
    async (preferences: Partial<User['layoutPreferences']>) => {
      if (!user?._id) {
        console.warn('Cannot update layout preferences: user not authenticated');
        return;
      }

      try {
        // Merge with existing preferences
        const updatedPreferences: User['layoutPreferences'] = {
          ...layoutPreferences,
          ...preferences,
          minWidths: {
            ...layoutPreferences.minWidths,
            ...preferences.minWidths,
          },
        };

        // Update local user state immediately
        dispatch(
          updateUser({
            layoutPreferences: updatedPreferences,
          })
        );

        // Trigger debounced DB save
        await userLayoutService.updateUserLayoutPreferences(
          user._id,
          updatedPreferences
        );
      } catch (error) {
        console.error('Failed to update layout preferences:', error);
        // You might want to show a toast notification here
      }
    },
    [user?._id, layoutPreferences, dispatch]
  );

  return {
    layoutPreferences,
    minWidths,
    updateLayoutPreferences,
    sidebarWidth: layoutPreferences.sidebarWidth ?? DEFAULT_SIDEBAR_WIDTH,
    knowledgebaseWidth: layoutPreferences.knowledgebaseWidth ?? DEFAULT_KNOWLEDGEBASE_WIDTH,
  };
};


